import React, { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ReviveBuyDialog } from "./ReviveBuyDialog";
import { OryvitaBuyDialog } from "./OryvitaBuyDialog";
import { FEATURES } from "@/config/features";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_ELECTROLYTES_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_lifestyle-essentials-3/artifacts/uo6vn1on_image.png";
const PRODUCT_SHIRATAKI_IMG = "/images/oryvita-1.jpg";

const ProductBlock = ({
  index,
  align = "left",
  overline,
  title,
  shortName,
  pitch,
  benefits,
  microcopy,
  image,
  imageAlt,
  priceAED,
  priceUnit = "",
  productId,
  testId,
  imageFit = "cover",
  imageBg = "bg-bone",
  imageAspect = "aspect-[5/4]",
  onBuyOverride,
}) => {
  const [loading, setLoading] = useState(false);
  const isLeftImage = align === "left";

  const onBuy = async () => {
    if (onBuyOverride) {
      onBuyOverride();
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        product_id: productId,
        quantity: 1,
      });
      if (data?.redirect_url) {
        window.location.assign(data.redirect_url);
      } else {
        toast.error("Could not start checkout — no redirect URL returned.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Checkout is not available right now. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <article
      data-testid={testId}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
    >
      <div className={`lg:col-span-7 ${isLeftImage ? "lg:order-1" : "lg:order-2"}`}>
        <div className={`relative ${imageAspect} w-full overflow-hidden ${imageBg} rounded-sm`}>
          <img
            src={image}
            alt={imageAlt}
            className={`w-full h-full ${imageFit === "contain" ? "object-contain p-6 md:p-10" : "object-cover"} transition-transform duration-[1200ms] ease-out hover:scale-[1.03]`}
            loading="lazy"
            style={{
              WebkitMaskImage:
                "radial-gradient(ellipse 100% 100% at 50% 50%, black 70%, transparent 100%)",
              maskImage:
                "radial-gradient(ellipse 100% 100% at 50% 50%, black 70%, transparent 100%)",
            }}
          />
          {productId === "field-salts" && false && (
            <div
              aria-hidden="true"
              data-testid="revive-card-shopnow-cover"
              className="absolute rounded-full bg-black pointer-events-none"
              style={{
                left: "50%",
                top: "56%",
                width: "16%",
                height: "9%",
                transform: "translate(-50%, -50%)",
              }}
            />
          )}
          <div className="absolute top-4 left-4 bg-bone/90 backdrop-blur-sm px-3 py-1 rounded-sm">
            <span className="overline text-ink">No. {index}</span>
          </div>
          <div
            className="absolute bottom-4 left-4 bg-ink text-bone px-4 py-2 rounded-sm"
            data-testid={`${testId}-price`}
          >
            <span className="overline text-bone/60 block leading-none">From</span>
            <span className="font-display font-bold text-lg leading-tight">
              AED {priceAED}
              {priceUnit && (
                <span className="text-bone/70 font-medium text-sm ml-1">
                  {priceUnit}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className={`lg:col-span-5 ${isLeftImage ? "lg:order-2" : "lg:order-1"}`}>
        <div className="overline text-terracotta mb-4">{overline}</div>
        <h3 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-ink leading-[1.05]">
          {title}
        </h3>
        <p className="mt-5 text-base md:text-lg text-ink/75 leading-relaxed">
          {pitch}
        </p>

        <ul className="mt-8 space-y-3 border-t border-hairline pt-6">
          {benefits.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-4 group"
              data-testid={`${testId}-benefit-${i}`}
            >
              <span className="font-display font-black text-terracotta text-xs tracking-[0.2em] uppercase shrink-0 w-24 pt-0.5">
                {b.label}
              </span>
              <span className="text-ink/80 font-body text-sm md:text-base leading-relaxed">
                {b.value}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-8 bg-surfacealt border border-hairline px-5 py-4 rounded-sm">
          <span className="overline text-ink block mb-1">Why we included it</span>
          <p className="text-sm text-inkmute font-body leading-relaxed">{microcopy}</p>
        </div>

        <div className="mt-8 flex items-center gap-5">
          <button
            onClick={onBuy}
            disabled={loading}
            data-testid={`${testId}-cta`}
            className="group inline-flex items-center gap-2 bg-ink text-bone hover:bg-terracotta transition-colors duration-300 px-6 py-3.5 rounded-sm font-display font-semibold text-sm disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Starting checkout…
              </>
            ) : (
              <>
                Buy {shortName} — AED {priceAED}
                {priceUnit && <span className="opacity-80 ml-1">{priceUnit}</span>}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
          <a
            href="#contact"
            data-testid={`${testId}-enquire`}
            className="overline text-ink link-underline hover:text-terracotta"
          >
            Enquire
          </a>
        </div>

        <p className="mt-3 text-xs text-inkmute font-body" data-testid={`${testId}-payment-note`}>
          Secure UAE checkout · Powered by Ziina
        </p>
      </div>
    </article>
  );
};

export const FeaturedProducts = () => {
  const [reviveOpen, setReviveOpen] = useState(false);
  const [reviveFlavour, setReviveFlavour] = useState(null);
  const [oryvitaOpen, setOryvitaOpen] = useState(false);

  React.useEffect(() => {
    const onOpen = (e) => {
      setReviveFlavour(e?.detail?.flavour || null);
      setReviveOpen(true);
    };
    window.addEventListener("revive:open-buy", onOpen);
    return () => window.removeEventListener("revive:open-buy", onOpen);
  }, []);

  return (
    <section
      id="products"
      data-testid="featured-products-section"
      className="py-24 md:py-32 bg-bone"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <header className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="overline text-terracotta mb-4">The Phase 01 Range</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight text-ink leading-[1.02]">
              {FEATURES.showRevive ? "Two products." : "One product."}{" "}
              <br className="hidden md:block" />
              <span className="font-wordmark font-semibold italic text-terracotta">
                Hand-picked,
              </span>{" "}
              not algorithm-picked.
            </h2>
          </div>
          <p className="md:max-w-sm text-ink/70 text-base leading-relaxed">
            {FEATURES.showRevive
              ? "We launched with two essentials we use ourselves. Both fill a gap the UAE shelf still hasn’t."
              : "We start with one essential we use ourselves — the smarter rice alternative that UAE shelves still don’t stock consistently. More considered goods are on the way."}
          </p>
        </header>

        <div className="space-y-28 md:space-y-36">
          {FEATURES.showRevive && (
            <ProductBlock
              index="01"
              align="left"
              overline="Sports Hydration · 30 sachet box"
              title="REVIVE — Daily Electrolytes"
              shortName="REVIVE"
              pitch="The perfect hydration formula. Sourced through Thrive by Madsen — nine flavours, one sachet per serving, designed for the Gulf climate and the long, hot day."
              onBuyOverride={() => setReviveOpen(true)}
              benefits={[
                { label: "1000mg Sodium", value: "Replaces what you sweat out in heat — without the bloat." },
                { label: "200mg Potassium", value: "Supports muscle function across high-output sessions." },
                { label: "100mg Magnesium", value: "Aids recovery and helps you sleep like an adult." },
                { label: "Zero Sugar", value: "No carbs, no calories, no anti-caking agents. Nothing artificial." },
              ]}
              microcopy="Made with Oryx Kalahari salt and clean mineral salts (sodium chloride, potassium chloride, magnesium malate). The only daily electrolyte we’d carry."
              image={PRODUCT_ELECTROLYTES_IMG}
              imageAlt="REVIVE Daily Electrolytes — two athletes hydrating"
              priceAED="160"
              productId="field-salts"
              testId="product-card-electrolytes"
            />
          )}

          <ProductBlock
            index={FEATURES.showRevive ? "02" : "01"}
            align={FEATURES.showRevive ? "right" : "left"}
            overline="Healthy Rice Alternative · 1kg"
            title="Oryvita — Dry Shirataki Rice"
            shortName="Oryvita"
            pitch="The smarter rice alternative. Plant-based, gluten-free, sugar-free — a featherlight swap with just 41 calories per serving, made from konjac and rice flour."
            benefits={[
              { label: "41 kcal", value: "Calorically near-invisible per serving. Volume without the load." },
              { label: "Konjac + Rice", value: "Glucomannan fibre keeps you full; rice flour keeps the texture honest." },
              { label: "Plant-based", value: "Gluten-free, sugar-free, vegan-friendly. Works with how you train." },
              { label: "Ready Fast", value: "Dry pack — rinse, simmer, plate. Done in minutes, every time." },
            ]}
            microcopy="Hard to find in UAE supermarkets, and when you do, the brands are inconsistent. Oryvita is the one we trust and keep in stock — 1 kg dry packs."
            image={PRODUCT_SHIRATAKI_IMG}
            imageAlt="Oryvita Dry Shirataki Rice 1kg — the smarter rice alternative"
            priceAED="49.95"
            priceUnit="/ kg"
            productId="shirataki-rice"
            testId="product-card-shirataki"
            imageFit="contain"
            imageBg="bg-bone"
            imageAspect="aspect-[4/5]"
            onBuyOverride={() => setOryvitaOpen(true)}
          />
        </div>
      </div>

      {FEATURES.showRevive && (
        <ReviveBuyDialog
          open={reviveOpen}
          onOpenChange={setReviveOpen}
          initialFlavour={reviveFlavour}
        />
      )}
      <OryvitaBuyDialog open={oryvitaOpen} onOpenChange={setOryvitaOpen} />
    </section>
  );
};
